import { useEffect, useState, useMemo } from 'react';
import { usePostProcessingMetrics, useNamedEntities, type TagMetric } from '../../hooks/usePostProcessingMetrics';
import { broadcastBasicSearch } from '../../services/angular-bridge';
import { fieldContains, dateRangeContains } from '../../utilities/searchSyntax';
import { PieChartPanel } from '../../components/charts/PieChartPanel';
import { BarChartPanel } from '../../components/charts/BarChartPanel';
import { DocumentTimeline } from '../../components/charts/DocumentTimeline';
import { ChordDiagram } from '../../components/charts/ChordDiagram';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { SEMANTIC_ENTITY_TYPES, DEFAULT_ENTITY_TYPE_ID } from '../../constants/entityTypes';

type DataSelection = 'Extension' | 'Category';

function tagMetricsToBar(tags: TagMetric[]): { label: string; value: number }[] {
  return tags.map((t) => ({ label: t.value.key, value: t.value.value }));
}

/**
 * Per-collection Post-Processing overview — the React port of collection.html's second
 * tab, mirroring the project-level PostProcessingTab but scoped to one collection: the
 * Data Overview offers only Extension/Category (the Collection/Custodian facets are
 * project-level), and there's no project framing or report button.
 */
export function CollectionPostProcessingTab({
  projectId,
  collectionId,
}: {
  projectId: string;
  collectionId: string;
}) {
  const { data: metrics, isLoading } = usePostProcessingMetrics(projectId, collectionId);

  const [dataSelection, setDataSelection] = useState<DataSelection>('Extension');
  const [entityType, setEntityType] = useState(
    SEMANTIC_ENTITY_TYPES.find((t) => t.id === DEFAULT_ENTITY_TYPE_ID) ?? SEMANTIC_ENTITY_TYPES[0]);
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const [viewRange, setViewRange] = useState<[Date, Date] | null>(null);

  useEffect(() => {
    if (metrics) setViewRange([new Date(metrics.startDate), new Date(metrics.endDate)]);
  }, [metrics?.startDate, metrics?.endDate]);

  // Collection scope (context 0) so the search bar shows "This collection".
  const handleSearch = (query: string) => broadcastBasicSearch(query, 0);
  const searchDateRange = (start: Date, end: Date) =>
    broadcastBasicSearch(dateRangeContains('createdDate', start, end), 0);
  const handleViewDocuments = () => {
    const [start, end] = viewRange ?? [new Date(metrics!.startDate), new Date(metrics!.endDate)];
    searchDateRange(start, end);
  };

  const sizeData = dataSelection === 'Extension' ? (metrics?.extensionBySize ?? []) : (metrics?.typeBySize ?? []);
  const countData = dataSelection === 'Extension' ? (metrics?.extensionByCount ?? []) : (metrics?.typeByCount ?? []);

  const emailDomains = (metrics?.emailDomains ?? []).map((d) => ({ label: d.key, value: d.value }));
  const systemTags = tagMetricsToBar(metrics?.systemTags ?? []);
  const systemTagIdByLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of metrics?.systemTags ?? []) map.set(t.value.key, t.id);
    return map;
  }, [metrics?.systemTags]);
  // The aggregate metrics response only carries `person` entities; any other
  // type needs its own fetch (scoped to this collection).
  const isDefaultEntityType = entityType.id === DEFAULT_ENTITY_TYPE_ID;
  const { data: fetchedEntities, isFetching: isFetchingEntities } =
    useNamedEntities(projectId, entityType.id, !isDefaultEntityType, collectionId);
  const entityData = tagMetricsToBar(
    isDefaultEntityType ? (metrics?.namedEntities ?? []) : (fetchedEntities ?? [])
  );

  if (isLoading) return <LoadingPlaceholder />;

  return (
    <>
      {/* Data Overview */}
      <div className="row">
        <h2>Data Overview</h2>
        <div className="btn-group">
          {(['Extension', 'Category'] as DataSelection[]).map((s) => (
            <label key={s} className={`btn btn-default${dataSelection === s ? ' active' : ''}`} onClick={() => setDataSelection(s)} data-testid="collection-home-post-processing-data-btn">
              {s}
            </label>
          ))}
        </div>
        {(sizeData.length > 0 || countData.length > 0) && (
          <div style={{ marginTop: 44 }}>
            <PieChartPanel key={`size-${dataSelection}`} title={`${dataSelection} by Size`} data={sizeData} valueType="bytes"
              onItemClick={(label) => handleSearch(fieldContains(dataSelection === 'Extension' ? 'fileExtension' : 'category', label))} />
            <div className="col-md-2" />
            <PieChartPanel key={`count-${dataSelection}`} title={`${dataSelection} by Count`} data={countData} valueType="count"
              onItemClick={(label) => handleSearch(fieldContains(dataSelection === 'Extension' ? 'fileExtension' : 'category', label))} />
          </div>
        )}
      </div>

      {/* Document Timeline */}
      <div className="row" style={{ marginTop: 96 }}>
        <div style={{ display: 'table' }}>
          <div style={{ display: 'table-cell' }}><h2>Document Timeline [</h2></div>
          <div style={{ display: 'table-cell', verticalAlign: 'middle', paddingTop: 15 }}>
            <a onClick={handleViewDocuments} style={{ cursor: 'pointer' }} data-testid="collection-home-view-docs-link">View Documents in Range</a>
          </div>
          <div style={{ display: 'table-cell' }}><h2>]</h2></div>
        </div>
        <div className="row">
          <div className="col-md-12" style={{ paddingRight: 20 }}>
            {metrics && (metrics.documentCountByDate ?? []).length > 0 ? (
              <DocumentTimeline
                data={metrics.documentCountByDate}
                onRangeChange={(start, end) => setViewRange([start, end])}
                onDateClick={(date) => searchDateRange(date, date)}
              />
            ) : (
              <p className="text-muted">No document date data available.</p>
            )}
          </div>
        </div>
      </div>

      {/* Email Domains */}
      {emailDomains.length > 0 && (
        <div className="row" style={{ marginTop: 96 }}>
          <h2>Email Domains</h2>
          <div className="row"><div className="col-md-12">
            <BarChartPanel key="emailDomains" data={emailDomains} onItemClick={(label) => handleSearch(fieldContains('emailDomains', label))} />
          </div></div>
        </div>
      )}

      {/* System Tags */}
      {systemTags.length > 0 && (
        <div className="row" style={{ marginTop: 96 }}>
          <h2>System Tags</h2>
          <div className="row"><div className="col-md-12">
            <BarChartPanel key="systemTags" data={systemTags}
              onItemClick={(label) => handleSearch(fieldContains('systemTags', systemTagIdByLabel.get(label) ?? label))} />
          </div></div>
        </div>
      )}

      {/* Named Entities */}
      <div className="row" style={{ marginTop: 96 }}>
        <div>
          <h2>Named Entities</h2>
          <div className={`btn-group${entityDropdownOpen ? ' open' : ''}`}>
            <button className="btn btn-default" onClick={() => setEntityDropdownOpen((o) => !o)} data-testid="collection-home-entity-btn">
              {entityType.name} <span className="caret" />
            </button>
            <ul className="dropdown-menu" data-testid="collection-home-entity-menu">
              {SEMANTIC_ENTITY_TYPES.map((t) => (
                <li key={t.id}><a onClick={() => { setEntityType(t); setEntityDropdownOpen(false); }} data-testid="collection-home-entity-menu-item">{t.name}</a></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="row"><div className="col-md-12">
          {entityData.length > 0 ? (
            <BarChartPanel key={`entities-${entityType.id}`} data={entityData}
              onItemClick={(label) => handleSearch(fieldContains(`${entityType.field}Entities`, label))} />
          ) : isFetchingEntities ? (
            <p className="text-muted">Loading {entityType.name.toLowerCase()} entities…</p>
          ) : (
            <p className="text-muted">No {entityType.name.toLowerCase()} entities found.</p>
          )}
        </div></div>
      </div>

      {/* Communication Chart */}
      {metrics?.communicationPatterns?.[0] && (
        <div className="row" style={{ marginTop: 96 }}>
          <div>
            <h2>Communication Chart</h2>
            <ChordDiagram
              pattern={metrics.communicationPatterns[0]}
              onGroupClick={(group) => handleSearch(`${fieldContains('emailFrom', group)} OR ${fieldContains('emailRecipients', group)}`)}
            />
          </div>
        </div>
      )}
    </>
  );
}
